import {
  getAudienceUsers,
  searchAudienceGroups,
  type AudienceGroup,
  type AudienceUser,
} from "../helpers/audience-api";

type Translate = (key: string, params?: Record<string, string | number>) => string;

export interface AudienceState {
  targetAll: boolean;
  groups: AudienceGroup[];
  isOrgDefaultSignature?: boolean;
  isOrgReplyForwardDefaultSignature?: boolean;
}

export interface AudiencePickerOptions {
  showOrgDefaults?: boolean;
}

export interface AudiencePicker {
  getState: () => AudienceState;
  setState: (state: AudienceState) => void;
}

const CHEVRON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>`;

const COLLAPSE_ICON = `<svg class="sunnysign-mapping-box__collapse-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="7 13 12 18 17 13"/><polyline points="7 6 12 11 17 6"/></svg>`;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function highlightMatch(value: string, query: string): string {
  const needle = query.trim();
  if (!needle) return escapeHtml(value);
  const lowerValue = value.toLocaleLowerCase();
  const lowerNeedle = needle.toLocaleLowerCase();
  let position = 0;
  let html = "";
  while (position < value.length) {
    const match = lowerValue.indexOf(lowerNeedle, position);
    if (match < 0) {
      html += escapeHtml(value.slice(position));
      break;
    }
    html += escapeHtml(value.slice(position, match));
    html += `<mark class="sunnysign-audience-match">${escapeHtml(value.slice(match, match + needle.length))}</mark>`;
    position = match + needle.length;
  }
  return html;
}

function groupRow(group: AudienceGroup, query: string, checked: boolean): string {
  return `<label class="sunnysign-audience-row">
    <input type="checkbox" data-group-id="${escapeHtml(group.id)}"${checked ? " checked" : ""} />
    <span class="sunnysign-audience-row__avatar">${escapeHtml((group.displayName || "?").slice(0, 2).toUpperCase())}</span>
    <span class="sunnysign-audience-row__main"><strong>${highlightMatch(group.displayName, query)}</strong><small>${escapeHtml(group.groupType)}</small></span>
    <code>${highlightMatch(group.id, query)}</code>
  </label>`;
}

function createDialog(title: string): { overlay: HTMLDivElement; panel: HTMLDivElement; close: () => void } {
  const overlay = document.createElement("div");
  overlay.className = "sunnysign-audience-dialog-overlay";
  const panel = document.createElement("div");
  panel.className = "sunnysign-audience-dialog";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.innerHTML = `
    <header class="sunnysign-audience-dialog__header">
      <h3>${escapeHtml(title)}</h3>
      <button type="button" class="sunnysign-audience-dialog__close" aria-label="Close">×</button>
    </header>`;
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  const close = (): void => overlay.remove();
  panel.querySelector(".sunnysign-audience-dialog__close")?.addEventListener("click", close);
  overlay.addEventListener("mousedown", (event) => {
    if (event.target === overlay) close();
  });
  return { overlay, panel, close };
}

async function openGroupDialog(
  initial: AudienceGroup[],
  t: Translate
): Promise<AudienceGroup | null> {
  const { panel, close } = createDialog(t("audience.selectGroups"));
  let selected = initial[0] ?? null;
  panel.insertAdjacentHTML(
    "beforeend",
    `<div class="sunnysign-audience-dialog__content">
      <input class="sunnysign-input sunnysign-audience-dialog__search" type="search" placeholder="${escapeHtml(t("audience.searchGroups"))}" />
      <p class="sunnysign-audience-dialog__count"></p>
      <div class="sunnysign-audience-dialog__list"></div>
      <div class="sunnysign-audience-dialog__sentinel" aria-hidden="true"></div>
    </div>
    <footer class="sunnysign-audience-dialog__actions">
      <button type="button" class="sunnysign-btn sunnysign-btn--secondary" data-action="cancel">${escapeHtml(t("form.cancel"))}</button>
      <button type="button" class="sunnysign-btn sunnysign-btn--primary" data-action="confirm">${escapeHtml(t("audience.confirm"))}</button>
    </footer>`
  );
  const input = panel.querySelector(".sunnysign-audience-dialog__search") as HTMLInputElement;
  const list = panel.querySelector(".sunnysign-audience-dialog__list") as HTMLDivElement;
  const sentinel = panel.querySelector(".sunnysign-audience-dialog__sentinel") as HTMLDivElement;
  const content = panel.querySelector(".sunnysign-audience-dialog__content") as HTMLDivElement;
  const count = panel.querySelector(".sunnysign-audience-dialog__count") as HTMLParagraphElement;
  const confirm = panel.querySelector('[data-action="confirm"]') as HTMLButtonElement;
  let requestVersion = 0;
  let timer: number | null = null;
  let nextPageToken: string | null = null;
  let loading = false;
  const loaded = new Map<string, AudienceGroup>();

  const bindRows = (): void => {
    list.querySelectorAll<HTMLInputElement>("[data-group-id]:not([data-bound])").forEach((checkbox) => {
      checkbox.dataset.bound = "true";
      const group = loaded.get(checkbox.dataset.groupId ?? "");
      if (!group) return;
      checkbox.addEventListener("change", () => {
        if (checkbox.checked) {
          list.querySelectorAll<HTMLInputElement>("[data-group-id]:checked").forEach((other) => {
            if (other !== checkbox) other.checked = false;
          });
          selected = group;
        } else if (selected?.id === group.id) {
          selected = null;
        }
        confirm.disabled = selected === null;
      });
    });
  };

  const load = async (reset = false): Promise<void> => {
    if (loading && !reset) return;
    const version = reset ? ++requestVersion : requestVersion;
    if (reset) {
      nextPageToken = null;
      loaded.clear();
      list.innerHTML = `<p class="sunnysign-audience-dialog__loading">${escapeHtml(t("form.loading"))}</p>`;
    } else if (!nextPageToken) {
      return;
    }
    loading = true;
    sentinel.classList.add("sunnysign-audience-dialog__sentinel--loading");
    try {
      const query = input.value.trim();
      const page = await searchAudienceGroups(query, reset ? null : nextPageToken);
      if (version !== requestVersion) return;
      if (reset) list.innerHTML = "";
      const fresh = page.items.filter((group) => !loaded.has(group.id));
      fresh.forEach((group) => loaded.set(group.id, group));
      list.insertAdjacentHTML(
        "beforeend",
        fresh.map((group) => groupRow(group, query, selected?.id === group.id)).join("")
      );
      nextPageToken = page.nextPageToken ?? null;
      count.textContent = t("audience.groupsFound", { count: page.totalCount ?? loaded.size });
      bindRows();
    } catch (error) {
      if (version === requestVersion) {
        list.innerHTML = `<p class="sunnysign-audience-dialog__error">${escapeHtml(String(error))}</p>`;
        nextPageToken = null;
      }
    } finally {
      if (version === requestVersion) {
        loading = false;
        sentinel.classList.remove("sunnysign-audience-dialog__sentinel--loading");
        sentinel.hidden = !nextPageToken;
      }
    }
  };

  input.addEventListener("input", () => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => void load(true), 250);
  });
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void load(false);
    },
    { root: content, rootMargin: "120px" }
  );
  observer.observe(sentinel);
  confirm.disabled = selected === null;
  void load(true);

  return new Promise((resolve) => {
    panel.querySelector('[data-action="cancel"]')?.addEventListener("click", () => {
      close();
      observer.disconnect();
      resolve(null);
    });
    confirm.addEventListener("click", () => {
      if (!selected) return;
      close();
      observer.disconnect();
      resolve(selected);
    });
  });
}

function userRow(user: AudienceUser, query: string): string {
  const secondary = user.userPrincipalName || user.mail || "";
  return `<div class="sunnysign-audience-row sunnysign-audience-row--user">
    <span class="sunnysign-audience-row__avatar">${escapeHtml((user.displayName || "?").slice(0, 2).toUpperCase())}</span>
    <span class="sunnysign-audience-row__main"><strong>${highlightMatch(user.displayName, query)}</strong><small>${highlightMatch(secondary, query)}</small></span>
  </div>`;
}

// Bewusst vorerst nicht in der Oberfläche verdrahtet; die Benutzerlisten-Funktion bleibt für eine spätere Nutzung erhalten.
async function openUsersDialog(groupIds: string[], t: Translate): Promise<void> {
  const { panel } = createDialog(t("audience.checkUsers"));
  if (groupIds.length === 0) {
    panel.insertAdjacentHTML(
      "beforeend",
      `<div class="sunnysign-audience-dialog__content">
        <p class="sunnysign-audience-dialog__hint">${escapeHtml(t("audience.allUsersHint"))}</p>
      </div>`
    );
    return;
  }

  panel.insertAdjacentHTML(
    "beforeend",
    `<div class="sunnysign-audience-dialog__content">
      <input class="sunnysign-input sunnysign-audience-dialog__search" type="search" placeholder="${escapeHtml(t("audience.searchUsers"))}" />
      <p class="sunnysign-audience-dialog__count"></p>
      <div class="sunnysign-audience-dialog__list"></div>
      <div class="sunnysign-audience-dialog__sentinel" aria-hidden="true"></div>
    </div>`
  );
  const input = panel.querySelector(".sunnysign-audience-dialog__search") as HTMLInputElement;
  const list = panel.querySelector(".sunnysign-audience-dialog__list") as HTMLDivElement;
  const sentinel = panel.querySelector(".sunnysign-audience-dialog__sentinel") as HTMLDivElement;
  const content = panel.querySelector(".sunnysign-audience-dialog__content") as HTMLDivElement;
  const count = panel.querySelector(".sunnysign-audience-dialog__count") as HTMLParagraphElement;
  let nextPageToken: string | null = null;
  let loading = false;
  let loadedCount = 0;
  let requestVersion = 0;
  let timer: number | null = null;

  const load = async (reset = false): Promise<void> => {
    if (loading && !reset) return;
    const version = reset ? ++requestVersion : requestVersion;
    if (reset) {
      nextPageToken = null;
      loadedCount = 0;
      list.innerHTML = `<p class="sunnysign-audience-dialog__loading">${escapeHtml(t("form.loading"))}</p>`;
    } else if (!nextPageToken) {
      return;
    }
    loading = true;
    sentinel.classList.add("sunnysign-audience-dialog__sentinel--loading");
    try {
      const query = input.value.trim();
      const page = await getAudienceUsers(groupIds, query, reset ? null : nextPageToken, 50);
      if (version !== requestVersion) return;
      if (reset) list.innerHTML = "";
      list.insertAdjacentHTML("beforeend", page.items.map((user) => userRow(user, query)).join(""));
      loadedCount += page.items.length;
      nextPageToken = page.nextPageToken ?? null;
      count.textContent = t("audience.usersFound", { count: page.totalCount ?? loadedCount });
    } catch (error) {
      if (version === requestVersion) {
        list.innerHTML = `<p class="sunnysign-audience-dialog__error">${escapeHtml(String(error))}</p>`;
        nextPageToken = null;
      }
    } finally {
      if (version === requestVersion) {
        loading = false;
        sentinel.classList.remove("sunnysign-audience-dialog__sentinel--loading");
        sentinel.hidden = !nextPageToken;
      }
    }
  };
  input.addEventListener("input", () => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => void load(true), 250);
  });
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void load(false);
    },
    { root: content, rootMargin: "120px" }
  );
  observer.observe(sentinel);
  await load(true);
}

export function mountAudiencePicker(
  containerId: string,
  t: Translate,
  initial: AudienceState,
  onChange?: () => void,
  options?: AudiencePickerOptions
): AudiencePicker | null {
  const container = document.getElementById(containerId);
  if (!container) return null;
  const showOrgDefaults = options?.showOrgDefaults === true;
  let state: AudienceState = {
    targetAll: initial.targetAll,
    groups: initial.targetAll ? [] : initial.groups.slice(0, 1),
    isOrgDefaultSignature: initial.isOrgDefaultSignature === true,
    isOrgReplyForwardDefaultSignature: initial.isOrgReplyForwardDefaultSignature === true,
  };
  const availabilityName = `sunnysign-availability-${containerId}`;
  const orgDefaultsHtml = showOrgDefaults
    ? `<div class="sunnysign-org-default-options">
          <label class="sunnysign-org-default-option">
            <input type="checkbox" data-org-default="new-mail"${state.isOrgDefaultSignature ? " checked" : ""} />
            <span>${escapeHtml(t("audience.orgDefaultNewMail"))}</span>
          </label>
          <label class="sunnysign-org-default-option">
            <input type="checkbox" data-org-default="reply-forward"${state.isOrgReplyForwardDefaultSignature ? " checked" : ""} />
            <span>${escapeHtml(t("audience.orgDefaultReplyForward"))}</span>
          </label>
        </div>`
    : "";
  container.innerHTML = `
    <div class="sunnysign-mapping-box sunnysign-mapping-box--collapsed sunnysign-audience-box">
      <button type="button" class="sunnysign-mapping-box__collapse-trigger" aria-expanded="false">
        <h3 class="sunnysign-mapping-box__title"></h3>
        <span class="sunnysign-mapping-box__collapse-icon">${COLLAPSE_ICON}</span>
      </button>
      <div class="sunnysign-mapping-box__body" hidden>
        <div class="sunnysign-availability-options">
          <label class="sunnysign-availability-option">
            <input type="radio" name="${escapeHtml(availabilityName)}" value="all" />
            <span>${escapeHtml(t("audience.all"))}</span>
          </label>
          <div class="sunnysign-availability-group-row">
            <label class="sunnysign-availability-option">
              <input type="radio" name="${escapeHtml(availabilityName)}" value="group" />
              <span>${escapeHtml(t("audience.group"))}</span>
            </label>
            <button type="button" class="sunnysign-audience-selection">${CHEVRON}</button>
          </div>
        </div>
        ${orgDefaultsHtml}
      </div>
    </div>`;
  const box = container.querySelector(".sunnysign-audience-box") as HTMLDivElement;
  const collapse = box.querySelector(".sunnysign-mapping-box__collapse-trigger") as HTMLButtonElement;
  const title = box.querySelector(".sunnysign-mapping-box__title") as HTMLHeadingElement;
  const body = box.querySelector(".sunnysign-mapping-box__body") as HTMLDivElement;
  const all = box.querySelector('input[value="all"]') as HTMLInputElement;
  const group = box.querySelector('input[value="group"]') as HTMLInputElement;
  const selection = box.querySelector(".sunnysign-audience-selection") as HTMLButtonElement;
  const orgDefaultNewMail = box.querySelector('[data-org-default="new-mail"]') as HTMLInputElement | null;
  const orgDefaultReplyForward = box.querySelector('[data-org-default="reply-forward"]') as HTMLInputElement | null;

  const refresh = (): void => {
    state.targetAll = state.groups.length === 0;
    title.textContent = t("audience.titleCount", {
      count: state.targetAll ? t("audience.all") : t("audience.group"),
    });
    all.checked = state.targetAll;
    group.checked = !state.targetAll;
    const selectionText = state.groups[0]?.displayName ?? t("audience.selectGroupButton");
    selection.innerHTML = `<span>${escapeHtml(selectionText)}</span>${CHEVRON}`;
  };
  collapse.addEventListener("click", () => {
    const open = body.hidden;
    body.hidden = !open;
    box.classList.toggle("sunnysign-mapping-box--collapsed", !open);
    collapse.setAttribute("aria-expanded", open ? "true" : "false");
  });
  all.addEventListener("change", () => {
    if (!all.checked) return;
    state.groups = [];
    refresh();
    onChange?.();
  });
  const selectGroup = async (): Promise<void> => {
    const picked = await openGroupDialog(state.groups, t);
    if (picked === null) return;
    state.groups = [picked];
    refresh();
    onChange?.();
  };
  group.addEventListener("change", () => {
    if (!group.checked || state.groups.length > 0) return;
    // "Gruppe" ist erst ein gültiger Zustand, wenn im Dialog tatsächlich eine Gruppe gewählt wurde.
    refresh();
    void selectGroup();
  });
  selection.addEventListener("click", () => void selectGroup());
  orgDefaultNewMail?.addEventListener("change", () => {
    state.isOrgDefaultSignature = orgDefaultNewMail.checked;
    onChange?.();
  });
  orgDefaultReplyForward?.addEventListener("change", () => {
    state.isOrgReplyForwardDefaultSignature = orgDefaultReplyForward.checked;
    onChange?.();
  });
  refresh();

  return {
    getState: () => ({
      targetAll: state.targetAll,
      groups: [...state.groups],
      isOrgDefaultSignature: state.isOrgDefaultSignature === true,
      isOrgReplyForwardDefaultSignature: state.isOrgReplyForwardDefaultSignature === true,
    }),
    setState: (next) => {
      state = {
        targetAll: next.targetAll,
        groups: next.targetAll ? [] : next.groups.slice(0, 1),
        isOrgDefaultSignature: next.isOrgDefaultSignature === true,
        isOrgReplyForwardDefaultSignature: next.isOrgReplyForwardDefaultSignature === true,
      };
      refresh();
      if (orgDefaultNewMail) orgDefaultNewMail.checked = state.isOrgDefaultSignature === true;
      if (orgDefaultReplyForward) orgDefaultReplyForward.checked = state.isOrgReplyForwardDefaultSignature === true;
    },
  };
}
